/**
 * "Arrange all", done by elkjs's layered algorithm.
 *
 * Loaded on demand: the bundle is about 1.6 MB and most visits to a diagram open
 * one somebody has already laid out. elkjs is taken under EPL-2.0, ruled fine for
 * an internal tool (best-decision-012).
 *
 * Machines are laid out WITH their states inside them - hierarchy handling set
 * to include children - so a transition that leaves a machine is routed as one
 * graph rather than as two layouts that disagree about where the machine is.
 */

import type { ElkExtendedEdge, ElkNode } from 'elkjs/lib/elk-api'
import type { LayoutBox, ViewState } from '../../schema'
import { LEAF_SIZE, MACHINE_HEADER, MACHINE_PADDING, initialSize, parentsOf } from './stateDiagram'
import type { TransitionEdge } from './stateDiagram'

// Room between states for an arrow each way and a label on it. Stated on EVERY
// level: ELK does not inherit spacing into a compound node's children, and
// without it a machine's states were laid out 20 units apart - too close for
// the two arrows of a loop to be told apart, or for a label to sit between them.
const SPACING = {
  'elk.spacing.nodeNode': '48',
  'elk.layered.spacing.nodeNodeBetweenLayers': '120',
}

const ROOT_OPTIONS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.hierarchyHandling': 'INCLUDE_CHILDREN',
  'elk.edgeRouting': 'ORTHOGONAL',
  ...SPACING,
}

const MACHINE_OPTIONS = {
  'elk.padding': `[top=${MACHINE_HEADER},left=${MACHINE_PADDING},bottom=${MACHINE_PADDING},right=${MACHINE_PADDING}]`,
  ...SPACING,
}

/**
 * The ELK graph for a view: states nested in the machines the view shows.
 *
 * Exported for its own test, because a state hung from the wrong parent here
 * lays out without complaint and draws outside its machine.
 */
export function elkGraph(states: ViewState[], edges: TransitionEdge[]): ElkNode {
  const parents = parentsOf(states)
  const nodes = new Map<string, ElkNode>(
    states.map((state) => [state.qualified_name, { id: state.qualified_name, children: [] }]),
  )
  const root: ElkNode = { id: '__root__', layoutOptions: ROOT_OPTIONS, children: [] }

  for (const state of states) {
    const node = nodes.get(state.qualified_name) as ElkNode
    const parent = parents.get(state.qualified_name)
    ;(parent ? (nodes.get(parent) as ElkNode) : root).children?.push(node)
  }
  const byName = new Map(states.map((state) => [state.qualified_name, state]))
  for (const node of nodes.values()) {
    if (node.children?.length) {
      node.layoutOptions = MACHINE_OPTIONS
    } else {
      // A leaf is given its size; a machine is sized by ELK to fit what it holds.
      delete node.children
      const size = initialSize(byName.get(node.id) ?? {})
      node.width = size.width
      node.height = size.height
    }
  }

  root.edges = edges.map(
    (edge): ElkExtendedEdge => ({ id: edge.id, sources: [edge.source], targets: [edge.target] }),
  )
  return root
}

/**
 * ELK's result as ABSOLUTE boxes. ELK places a child relative to its parent, as
 * React Flow does, and the layout file does not.
 */
export function absoluteBoxes(root: ElkNode): Record<string, LayoutBox> {
  const boxes: Record<string, LayoutBox> = {}
  const walk = (node: ElkNode, originX: number, originY: number) => {
    for (const child of node.children ?? []) {
      const x = originX + (child.x ?? 0)
      const y = originY + (child.y ?? 0)
      boxes[child.id] = {
        x: Math.round(x),
        y: Math.round(y),
        width: Math.round(child.width ?? LEAF_SIZE.width),
        height: Math.round(child.height ?? LEAF_SIZE.height),
      }
      walk(child, x, y)
    }
  }
  walk(root, 0, 0)
  return boxes
}

/**
 * What a failed fetch of the layout engine is reported as (issue #92).
 *
 * The chunk's name carries a hash that changes from build to build, so a tab
 * opened before a deploy asks for a file the server no longer has. The browser
 * says only "Failed to fetch dynamically imported module", which reads as a
 * broken diagram rather than as a page that needs reloading.
 */
export const ENGINE_UNAVAILABLE =
  'The layout engine could not be loaded. BEST has probably been updated since this page was opened: reload the page and try again.'

/** Lay the whole view out, answering an absolute box for every state. */
export async function arrange(
  states: ViewState[],
  edges: TransitionEdge[],
): Promise<Record<string, LayoutBox>> {
  let ELK: typeof import('elkjs/lib/elk.bundled.js').default
  try {
    ELK = (await import('elkjs/lib/elk.bundled.js')).default
  } catch {
    throw new Error(ENGINE_UNAVAILABLE)
  }
  const laidOut = await new ELK().layout(elkGraph(states, edges))
  return absoluteBoxes(laidOut)
}
